import { useNavigate } from 'react-router-dom';
import type { ExcludedItem, ItemState } from '@/shared/api/types';
import { Button } from '@/shared/ui';

/**
 * CU-09 paso 4 · lo que no computa, con el motivo y la acción que lo desbloquea.
 *
 * Un total que no dice qué dejó afuera miente por omisión. Y "no se pudo procesar" tampoco sirve:
 * cada estado tiene una causa distinta y una salida distinta, así que se nombran las dos.
 */
const REASONS: Record<ItemState, { why: string; action: string | null }> = {
  Pending: {
    why: 'Se está estimando. Entra al total en cuanto termine.',
    action: null,
  },
  Unresolved: {
    why: 'Ningún resolutor pudo con la descripción sola.',
    action: 'Agregar foto, código o producto',
  },
  Failed: {
    why: 'Se cortó del lado nuestro. Se reintenta solo.',
    action: 'Reintentar ahora',
  },
  // Los tres que sí computan no deberían llegar acá, pero la interfaz no adivina: si el servidor
  // los manda, los explica igual en vez de mostrar una fila muda.
  Resolved: { why: 'Estimado.', action: null },
  NeedsConfirmation: { why: 'Espera que lo confirmes.', action: 'Revisar' },
  Confirmed: { why: 'Confirmado.', action: null },
};

export function ExcludedList({ items }: { items: ExcludedItem[] }) {
  const navigate = useNavigate();

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="stack">
      <div className="row-between">
        <h2>No suman al total</h2>
        <span className="micro muted">y por qué</span>
      </div>

      <ul className="list-reset">
        {items.map((item) => {
          const reason = REASONS[item.state];

          return (
            <li key={item.mealItemId} className="card-dashed">
              <div className="row-between">
                <span className="item-name">{item.freeText ?? 'Ítem sin descripción'}</span>
              </div>
              <span className="micro muted">{reason.why}</span>
              {reason.action !== null && (
                <Button
                  variant="quiet"
                  onClick={() => navigate(`/comidas/${item.mealId}/items/${item.mealItemId}`)}
                >
                  {reason.action}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <p className="micro muted">
        Sólo suman los ítems con una estimación vigente. Preferimos un total incompleto y honesto
        antes que uno completo e inventado.
      </p>
    </section>
  );
}
